import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { delay } from 'rxjs/operators';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { MessageType } from '../enums/message-type';
import { NimbusUserSettings } from '../models/nimbus-user-settings';
import { OpenInBrowserMessage } from '../models/web-view-messages/open-in-browser-message';
import { OpenNewWindowMessage } from '../models/web-view-messages/open-new-window-message';
import { RedirectCurrentViewMessage } from '../models/web-view-messages/redirect-current-view-message';
import { ResizeCurrentWindowMessage } from '../models/web-view-messages/resize-current-window-message';
import { UpdateSettingsMessage } from '../models/web-view-messages/update-settings-message';
import { CenterCurrentWindowMessage } from '../models/web-view-messages/center-current-window-message';
import { RepositionCurrentWindowMessage } from '../models/web-view-messages/reposition-current-window-message';
import { VersionMessage } from '../models/web-view-messages/version-message';
import { WebViewMessage } from '../models/web-view-messages/web-view-message';
import { LoggingMessage } from '../models/web-view-messages/logging-message';
import { PreviewZoomMessage } from '../models/web-view-messages/preview-zoom-message';
import { TrayIcon } from '../enums/tray-icon';
import { ChangeTrayIconMessage } from '../models/web-view-messages/change-tray-icon-message';
import { BringToFrontMessage } from '../models/web-view-messages/bring-to-front-message';
import { ExtendTeamsToastMessage } from '../models/web-view-messages/extend-teams-toast-message';
import { OpenLogFolderMessage } from '../models/web-view-messages/open-log-folder-message';
import { TopmostMessage } from '../models/web-view-messages/topmost-message';
import { SettingsService } from './settings.service';
import { UserSettingsService } from './user-settings.service';
import { AuthService } from './auth.service';
import { LoggingService } from './logging.service';
import { TaskOutboundService } from './task-outbound.service';
import { TaskTransferService } from './task-transfer.service';
import { CommandBusService } from './command-bus.service';

@Injectable({
  providedIn: 'root',
})
export class MessageService {
  public static loggingService;
  public static translate: TranslateService;
  public static currentTrayIcon: TrayIcon = 1;
  public static currentHoverTextTranslationKey: TranslationKeys = TranslationKeys.GeneralLoggedIn;

  /** @deprecated use CommandBusService.commandCompleted$ */
  public get commandCompleted$() {
    return this.commandBus.commandCompleted$;
  }

  public constructor(
    private readonly router: Router,
    private readonly settingsService: SettingsService,
    private readonly userSettingsService: UserSettingsService,
    private readonly translateService: TranslateService,
    private readonly authService: AuthService,
    private readonly outboundService: TaskOutboundService,
    private readonly transferService: TaskTransferService,
    private readonly commandBus: CommandBusService,
    loggingService: LoggingService,
  ) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const webview = (window as any).chrome.webview;
    if (!webview) {
      return;
    }
    MessageService.loggingService = loggingService;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    webview.addEventListener('message', async (event: any) => {
      const message = event.data;
      switch ((message as WebViewMessage).messageType) {
        case MessageType.Version: {
          this.settingsService.setDesktopVersion((message as VersionMessage)?.version);
          break;
        }
        case MessageType.UpdateSettings: {
          this.userSettingsService.userSettings = (message as UpdateSettingsMessage).nimbusUserSettings;
          if (
            this.userSettingsService.hostSettingsReceived$.value &&
            (this.userSettingsService.defaultWidth !== this.userSettingsService.userSettings.windowWidth ||
              this.userSettingsService.defaultHeight !== this.userSettingsService.userSettings.windowHeight)
          ) {
            MessageService.SendUpdateSettings({
              ...this.userSettingsService.userSettings,
              windowWidth: this.userSettingsService.defaultWidth,
              windowHeight: this.userSettingsService.defaultHeight,
            } as NimbusUserSettings);
            MessageService.SendResizeWindow(this.userSettingsService.defaultWidth, this.userSettingsService.defaultHeight);
          }
          break;
        }
        case MessageType.LogOut: {
          // eslint-disable-next-line nimbus/prefer-until-destroyed
          this.authService.logoutPopup().subscribe();
          break;
        }
        case MessageType.Command: {
          this.commandBus.commandCompleted$.next(message);
          break;
        }
        case MessageType.UrlProtocolMessage: {
          this.outboundService.processClickToCall(message);
          break;
        }
        default:
          break;
      }
    });
    MessageService.translate = this.translateService;
    MessageService.SendMessage(new WebViewMessage(MessageType.Version));
    MessageService.SendResizeWindow(this.userSettingsService.defaultWidth, this.userSettingsService.defaultHeight);
  }

  public sendRepositionCurrentWindow(useLastKnownPosition: boolean = false): void {
    MessageService.SendMessage(new RepositionCurrentWindowMessage(useLastKnownPosition));
    this.transferService.resizeWindowForCurrentCall();
    MessageService.SendUpdateSettings({
      ...this.userSettingsService.userSettings,
      settingsWidth: this.userSettingsService.defaultWidth,
      settingsHeight: this.userSettingsService.defaultHeight,
    });
  }

  public static SendMessage(message: WebViewMessage): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).chrome.webview.postMessage(message);
  }

  public static SendLogin(): void {
    this.SendMessage(new WebViewMessage(MessageType.LoggedIn));
    this.SendChangeTrayIconMessage(TrayIcon.Green, TranslationKeys.GeneralLoggedIn);
  }

  public static SendLogout(): void {
    this.SendMessage(new WebViewMessage(MessageType.LogOut));
    this.SendChangeTrayIconMessage(TrayIcon.Disconnected, TranslationKeys.GeneralPleaseLogin);
  }

  public static SendQuit(): void {
    this.SendMessage(new WebViewMessage(MessageType.Quit));
  }

  public static SendUpdateSettings(userSettings: NimbusUserSettings): void {
    this.SendMessage(new UpdateSettingsMessage(userSettings));
  }

  public static SendPreviewZoom(zoom: number): void {
    this.SendMessage(new PreviewZoomMessage(zoom));
  }

  public static SendCenterCurrentWindow(): void {
    this.SendMessage(new CenterCurrentWindowMessage());
  }

  public static SendResizeWindow(width: number | undefined, height: number | undefined): void {
    this.SendMessage(new ResizeCurrentWindowMessage(width, height));
  }

  public static SendOpenNewWindow(width: number, height: number, top: number, left: number, url: string): void {
    this.SendMessage(new OpenNewWindowMessage(width, height, top, left, url));
  }

  public static SendOpenInBrowser(message: OpenInBrowserMessage): void {
    this.SendMessage(message);
  }

  public static SendRedirectCurrentView(url: string): void {
    this.SendMessage(new RedirectCurrentViewMessage(url));
  }

  public static SendLoggingMessage(severity: string | undefined, timestamp: string | undefined, message: string | undefined): void {
    this.SendMessage(new LoggingMessage(severity, timestamp, message));
  }

  public static SendChangeTrayIconMessage(icon: TrayIcon, hoverTextTranslationKey: TranslationKeys): void {
    try {
      this.SendMessage(new ChangeTrayIconMessage(icon, MessageService.translate.instant(hoverTextTranslationKey)));
      MessageService.currentTrayIcon = icon;
      MessageService.currentHoverTextTranslationKey = hoverTextTranslationKey;
    } catch (e) {
      this.loggingService.Error(`Could not translate TrayIconMessage: ${JSON.stringify(e)}`);
    }
  }

  public static SendBringToFrontMessage(delayMs: number = 0): void {
    of(null)
      .pipe(delay(delayMs))
      // eslint-disable-next-line nimbus/prefer-until-destroyed
      .subscribe((_) => this.SendMessage(new BringToFrontMessage()));
  }

  public static SendOpenLogFolderMessage(): void {
    this.SendMessage(new OpenLogFolderMessage());
  }

  public static SendExtendTeamsToastMessage(windowHeight: number, start: boolean): void {
    this.SendMessage(new ExtendTeamsToastMessage(windowHeight, start));
  }

  public static SendTopmostMessage(topmost?: boolean): void {
    this.SendMessage(new TopmostMessage(topmost));
  }

  public static translateTrayIcon(): void {
    try {
      this.SendMessage(
        new ChangeTrayIconMessage(
          MessageService.currentTrayIcon,
          MessageService.translate.instant(MessageService.currentHoverTextTranslationKey),
        ),
      );
    } catch (e) {
      this.loggingService.Error(`Could not translate TrayIconMessage: ${JSON.stringify(e)}`);
    }
  }
}
